/**
 * Phase 6b — Sentry error monitoring.
 *
 * Wraps `@sentry/react` initialization behind an env-gated entry
 * point and bridges the existing `monitoring` reporter seam (Phase
 * 6a Slice 4) to Sentry's `captureException` / `captureMessage`
 * APIs.
 *
 * PII posture: this app is a USCIS form filler — applicants enter
 * SSNs, A-numbers, addresses, criminal history, etc. Sentry MUST
 * NOT capture these values in plaintext. The Replay integration is
 * configured with `maskAllText`, `maskAllInputs`, and
 * `blockAllMedia` so DOM text + input values + uploaded media are
 * always masked. Belt-and-suspenders: `beforeSend` redacts any
 * string longer than 50 chars from `event.extra` so callers who
 * forget the rule and pass user-entered values into
 * `Sentry.setExtra` / `setContext` don't accidentally leak.
 *
 * The function-replacement seam (`installSentryReporter`) keeps
 * existing call sites — `reportError`, `reportEvent`,
 * `installGlobalErrorHandlers` — pointed at the same monitoring
 * surface; only the underlying transport changes.
 *
 * Source-cited Sentry APIs (verified against
 * `node_modules/@sentry/react/build/types/index.d.ts`):
 *   - `init(options)` — bootstraps the SDK
 *   - `browserTracingIntegration()` — registers performance tracing
 *   - `replayIntegration(opts)` — registers Session Replay with
 *     PII-masking flags
 *   - `captureException(err, hint)` — reports a thrown error with
 *     optional `extra` context
 *   - `captureMessage(name, captureContext)` — reports a structured
 *     event. NOT used for `reportEvent` — see `addBreadcrumb` below.
 *   - `addBreadcrumb({ category, message, data, level })` — appends
 *     a breadcrumb to the in-memory ring buffer. Breadcrumbs are
 *     attached to the next `captureException` call as recent-user-
 *     activity context. Used for funnel telemetry (`reportEvent`)
 *     so info-level product events don't pollute the Issues feed.
 *     Sentry-by-design: breadcrumbs only surface when an actual
 *     error fires, giving "what was the user doing right before
 *     this crashed?" context without creating a separate issue per
 *     event. (Slice 39 fix; replaces the prior captureMessage path
 *     that surfaced 16 noise-level issues per week in the Issues
 *     feed.)
 */

import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { setMonitoringReporter, type IMonitoringReporter } from './monitoring';

export interface IInitSentryOptions {
  /** Sentry DSN. When undefined / empty, init is a no-op. */
  dsn: string | undefined;
  /** Build environment ('production' / 'development' / etc). */
  environment: string;
  /**
   * Build release identifier — typically a git SHA or semver tag
   * injected at build time (e.g., via Vite's `define` config).
   * Optional; when undefined, Sentry does not attach a release tag
   * to events (debugging is harder but functional).
   */
  release?: string;
}

/**
 * Threshold above which `beforeSend` redacts string values from
 * `event.extra`. Any field-value-looking content (SSN, A-number,
 * address fragments, free-text answers) tends to exceed this; short
 * keys like enum values and IDs slip under it.
 */
const REDACT_STRING_THRESHOLD = 50;

/**
 * Pattern-based PII detectors. Each matches a piece of structured
 * applicant data that MUST NOT leave the browser via Sentry. Used by
 * `redactPiiPatterns` to scrub strings before they ship.
 *
 * The patterns intentionally over-match by a bit (e.g., any 9-digit
 * sequence is treated as an SSN candidate) — a false redaction in a
 * Sentry payload is harmless; a false negative is a privacy incident.
 */
// Pattern order is significant — earlier entries win. Dates first so
// "1990-05-17" isn't caught by the loose phone pattern; emails next
// because the @-separator is the strongest signal; identifier patterns
// (SSN / USCIS account / A-Number) before phone so digit-shaped
// identifiers don't get tagged as phones; phone before the generic
// i94 alphanumeric pattern so pure-digit strings (which I-94 numbers
// technically allow) are correctly classified.
const PII_PATTERNS: ReadonlyArray<{ name: string; pattern: RegExp }> = [
  // Date in MM/DD/YYYY or YYYY-MM-DD — date of birth, marriage, etc.
  // Loose enough to catch most variants without grabbing single-digit
  // numbers.
  {
    name: 'date',
    pattern: /\b\d{4}-\d{2}-\d{2}\b|\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g,
  },
  // Email addresses — restricted to common email chars so URL-prefix
  // greedy matches don't redact the surrounding URL. We trade exotic
  // email-local-part syntax (e.g., `foo/bar@example.com`) for clean
  // boundaries inside URLs and query strings, where the @-anchor is
  // the only signal we actually need.
  {
    name: 'email',
    pattern: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
  },
  // SSN — XXX-XX-XXXX or 9 consecutive digits.
  { name: 'ssn', pattern: /\b\d{3}-?\d{2}-?\d{4}\b/g },
  // USCIS Online Account Number — 12 consecutive digits.
  { name: 'uscis_acct', pattern: /\b\d{12}\b/g },
  // USCIS A-Number — A followed by 7-9 digits.
  { name: 'alien_number', pattern: /\bA-?\d{7,9}\b/gi },
  // Phone numbers — 10+ consecutive digits, optionally separated by
  // -, ., (), spaces. Loose intentionally.
  { name: 'phone', pattern: /\b(?:\+?\d[\d\s.()-]{8,}\d)\b/g },
  // I-94 Arrival/Departure record number — 9-11 chars, alphanumeric
  // uppercase, must contain at least one LETTER (pure-digit I-94 numbers
  // already get caught by the phone/ssn/uscis_acct patterns above; this
  // lookahead avoids re-redacting them as i94).
  { name: 'i94', pattern: /\b(?=[A-Z0-9]*[A-Z])[A-Z0-9]{9,11}\b/g },
];

/**
 * Scrub PII patterns from a string, replacing each match with
 * `[redacted:<name>]`. Returns the (possibly modified) string.
 */
function redactPiiPatterns(input: string): string {
  let out = input;
  for (const { name, pattern } of PII_PATTERNS) {
    out = out.replace(pattern, `[redacted:${name}]`);
  }
  return out;
}

/**
 * Recursively scrub PII from an arbitrary value:
 *   - strings → pattern-match + length cap
 *   - arrays → scrub each item
 *   - plain objects → scrub each value (keys are kept verbatim — they
 *     are the developer's chosen labels, never user input)
 *   - everything else (numbers, booleans, null, undefined) → passthrough
 *
 * The recursion is depth-limited so a cyclic / very-deep payload can't
 * stall the SDK pipeline.
 */
function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > 8) return '[redacted:depth]';
  if (typeof value === 'string') {
    const patternScrubbed = redactPiiPatterns(value);
    if (patternScrubbed.length > REDACT_STRING_THRESHOLD) {
      return '[redacted:length]';
    }
    return patternScrubbed;
  }
  if (Array.isArray(value)) {
    return value.map(v => scrubValue(v, depth + 1));
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = scrubValue(v, depth + 1);
    }
    return out;
  }
  return value;
}

/**
 * Bootstrap Sentry with PII-safe defaults. Returns `true` when init
 * fired, `false` when DSN was missing (local dev without a Sentry
 * project) so the caller can skip reporter wiring.
 *
 * Idempotent at the call-site level — Sentry's own `init` is safe
 * to call once per page load; we don't guard against double-init
 * here because `main.tsx` only calls this once before mount.
 */
export function initSentry(opts: IInitSentryOptions): boolean {
  const { dsn, environment, release } = opts;
  if (typeof dsn !== 'string' || dsn.length === 0) {
    return false;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      // React Router v7 navigation tracing — page loads + route
      // transitions are named after the matched route pattern
      // (e.g., "/intake") instead of the raw URL. Per the official
      // Sentry React SDK skill, the v7 integration takes the
      // hooks + helpers as deps so the SDK stays decoupled from
      // a specific React version. We use react-router-dom v7;
      // re-exports from `react-router` for v7 are also fine.
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    // Deliberate override of the official skill default
    // (`replaysSessionSampleRate: 0.1`). USCIS form-fillers handle
    // SSN, A-number, criminal history; we keep replay opt-in to
    // error sessions only. The 0% sample value is intentional, not
    // a placeholder.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    // Deliberate override of the skill default (`sendDefaultPii:
    // true`). Same PII reasoning as above — IP addresses + headers
    // become PII on a filing form. Keep PII-off and let the
    // explicit `extra`/`tags` paths in our reporter handle context.
    sendDefaultPii: false,
    beforeSend(event) {
      // Defense-in-depth, layered:
      //   1. `event.extra` — deeply scrubbed (developer-supplied context)
      //   2. `event.tags` — scrubbed (key/value pairs, often filtered IDs
      //      but devs occasionally pass user-derived values)
      //   3. `event.contexts` — scrubbed (browser / device / OS contexts
      //      are fine, but a stray contexts.user.email would leak)
      //   4. `event.message` — pattern-redacted (an error message string
      //      can quote a user value: "Failed to validate SSN 123-45-6789")
      //   5. `event.exception.values[].value` — pattern-redacted (same
      //      reason as message; Error.message strings sometimes include
      //      the value that triggered them)
      //   6. `event.request.url` / `event.request.query_string` —
      //      pattern-redacted (a stray form value in a URL would leak)
      //   7. `event.breadcrumbs` — pattern-redacted on `data` AND
      //      `message` per breadcrumb (the funnel-telemetry path passes
      //      attribute payloads that *should* be enum-only but might
      //      drift).
      // We never modify `event.tags` keys, only the values.
      if (event.extra) {
        event.extra = scrubValue(event.extra) as Record<string, unknown>;
      }
      if (event.tags) {
        const tagsOut: Record<string, string | number | boolean | bigint | symbol | null | undefined> = {};
        for (const [k, v] of Object.entries(event.tags)) {
          tagsOut[k] = typeof v === 'string' ? redactPiiPatterns(v) : v;
        }
        event.tags = tagsOut;
      }
      if (event.contexts) {
        event.contexts = scrubValue(event.contexts) as typeof event.contexts;
      }
      if (typeof event.message === 'string') {
        event.message = redactPiiPatterns(event.message);
      }
      if (event.exception?.values) {
        for (const exc of event.exception.values) {
          if (typeof exc.value === 'string') {
            exc.value = redactPiiPatterns(exc.value);
          }
        }
      }
      if (event.request?.url) {
        event.request.url = redactPiiPatterns(event.request.url);
      }
      if (event.request?.query_string && typeof event.request.query_string === 'string') {
        event.request.query_string = redactPiiPatterns(event.request.query_string);
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(crumb => ({
          ...crumb,
          message:
            typeof crumb.message === 'string'
              ? redactPiiPatterns(crumb.message)
              : crumb.message,
          data: crumb.data
            ? (scrubValue(crumb.data) as typeof crumb.data)
            : crumb.data,
        }));
      }
      return event;
    },
    beforeBreadcrumb(crumb) {
      // Scrub at creation time so leaked PII doesn't sit in the
      // ring buffer waiting for the next error to ship it. The same
      // `redactPiiPatterns` / `scrubValue` runs in `beforeSend`, but
      // this earlier pass keeps the in-memory buffer clean if a
      // session is replayed on-error.
      if (typeof crumb.message === 'string') {
        crumb.message = redactPiiPatterns(crumb.message);
      }
      if (crumb.data) {
        crumb.data = scrubValue(crumb.data) as typeof crumb.data;
      }
      return crumb;
    },
  });

  return true;
}

/**
 * Wire the monitoring reporter (Phase 6a Slice 4) to Sentry so
 * existing `reportError` / `reportEvent` call sites land on the
 * Sentry transport. Call once after `initSentry` returns true.
 */
export function installSentryReporter(): void {
  const sentryReporter: IMonitoringReporter = {
    reportError(err, context) {
      Sentry.captureException(err, {
        extra: { ...(context ?? {}) },
      });
    },
    reportEvent(name, attrs) {
      // `addBreadcrumb` instead of
      // `captureMessage`. captureMessage creates an Issue in the
      // Sentry feed regardless of `level: 'info'`; addBreadcrumb
      // attaches the event to the in-memory ring buffer so it
      // surfaces as "user activity context" on the next real
      // captureException call. Net effect: zero noise issues from
      // funnel telemetry (screener_started, screener_outcome,
      // etc.) AND every real error gets a recent-activity timeline
      // attached for free. See Sentry docs:
      //   https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/
      Sentry.addBreadcrumb({
        category: 'product-event',
        message: name,
        data: { ...(attrs ?? {}) },
        level: 'info',
      });
    },
  };
  setMonitoringReporter(sentryReporter);
}
